
import { useCallback } from "react";
import { Movie } from "@/types/movie";
import { logState, isDebugMovie } from "@/utils/debugLogger";
import { usePendingActions } from "./usePendingActions";
import { useMovieStateCore } from "./useMovieStateCore";
import { useMovieLookup } from "./useMovieLookup";
import { useMovieLoading } from "./useMovieLoading";

/**
 * Unified hook for managing all movie state and operations with optimized local updates
 */
export const useUnifiedMovieState = () => {
  // Core movie loading and state
  const { allMovies, setAllMovies, isLoading, refetchMovies } = useMovieLoading();

  // Optimized update movie state function for immediate UI updates
  const updateMovieState = useCallback((tmdbId: number, props: Partial<Movie>) => {
    const shouldLog = isDebugMovie({ title: 'Debug Movie' } as Movie) || true;
    
    if (shouldLog) {
      logState(`🔄 [STATE] Immediate movie state update for tmdbId: ${tmdbId}`, {
        tmdbId,
        updates: Object.keys(props).join(', '),
        updateProps: props
      });
    }
    
    setAllMovies(prev => {
      const movieIndex = prev.findIndex(movie => movie.tmdbId === tmdbId);
      
      if (movieIndex === -1) {
        // Movie not found - add if we have enough data
        if (props.title && props.director) {
          const newMovie: Movie = {
            tmdbId,
            title: props.title,
            director: props.director,
            year: props.year || 0,
            tags: props.tags || [],
            favorite: props.favorite || false,
            planToWatch: props.planToWatch || false,
            posterPath: props.posterPath,
            backdropPath: props.backdropPath,
            overview: props.overview
          };
          
          if (shouldLog) {
            logState(`✅ [STATE] Adding new movie immediately: ${props.title}`, {
              tmdbId,
              newMovie
            });
          }
          
          return [...prev, newMovie];
        }
        
        return prev;
      }
      
      const updated = [...prev];
      const existingMovie = updated[movieIndex];
      const updatedMovie = { ...existingMovie, ...props };
      updated[movieIndex] = updatedMovie;
      
      if (shouldLog) {
        logState(`✅ [STATE] Updated movie immediately: ${existingMovie.title}`, {
          tmdbId: existingMovie.tmdbId,
          before: { favorite: existingMovie.favorite, planToWatch: existingMovie.planToWatch },
          after: { favorite: updatedMovie.favorite, planToWatch: updatedMovie.planToWatch },
          movieIndex
        });
      }
      
      return updated;
    });
  }, [setAllMovies]);

  // Initialize the pending actions system
  const {
    pendingActions,
    addPendingAction,
    removePendingAction
  } = usePendingActions(updateMovieState);

  // Organized movie state (favorites, unseen, watchlist) with filmography
  const { 
    favoriteMovies, 
    unseenDirectorMovies, 
    planToWatchMovies,
    combinedMovies,
    isFilmographyLoading 
  } = useMovieStateCore(allMovies);

  // Movie lookup with combined movies
  const { movieLookup, getVisualState, isPendingAction, getPendingAction } = useMovieLookup(combinedMovies, pendingActions);

  // Optimized addMovie function for immediate UI updates
  const addMovie = useCallback((movie: Movie) => {
    const shouldLog = isDebugMovie(movie) || true;
    
    if (shouldLog) {
      logState(`➕ [STATE] Adding movie immediately to collection: ${movie.title}`, {
        tmdbId: movie.tmdbId,
        favorite: movie.favorite,
        planToWatch: movie.planToWatch
      });
    }
    
    setAllMovies(prev => {
      const existingIndex = prev.findIndex(m => m.tmdbId === movie.tmdbId);
      
      if (existingIndex !== -1) {
        // Update existing movie immediately
        const updated = [...prev];
        const existingMovie = updated[existingIndex];
        updated[existingIndex] = { ...existingMovie, ...movie };
        
        if (shouldLog) {
          logState(`🔄 [STATE] Updated existing movie immediately: ${movie.title}`, {
            tmdbId: movie.tmdbId,
            before: { favorite: existingMovie.favorite, planToWatch: existingMovie.planToWatch },
            after: { favorite: movie.favorite, planToWatch: movie.planToWatch }
          });
        }
        
        return updated;
      } else {
        // Add new movie immediately
        const newCollection = [...prev, movie];
        
        if (shouldLog) {
          logState(`✅ [STATE] Added new movie immediately: ${movie.title}`, {
            tmdbId: movie.tmdbId,
            movieState: { favorite: movie.favorite, planToWatch: movie.planToWatch }
          });
        }
        
        return newCollection;
      }
    });
  }, [setAllMovies]);

  // Simplified CRUD operations
  const removeMovieFromState = useCallback((tmdbId: number) => {
    setAllMovies(prev => prev.filter(movie => movie.tmdbId !== tmdbId));
  }, [setAllMovies]);

  // Undo pending action
  const undoPendingAction = useCallback((actionId: string) => {
    const action = pendingActions.find(a => a.id === actionId);
    if (action) {
      action.undo();
      removePendingAction(actionId);
    }
  }, [pendingActions, removePendingAction]);

  // Legacy compatibility
  const recentlyRemovedMovies: any[] = [];
  const undoRemoval = () => {};

  return {
    // State
    allMovies,
    favoriteMovies,
    unseenDirectorMovies,
    planToWatchMovies,
    isLoading: isLoading || isFilmographyLoading,
    viewMode: "grid",
    movieLookup,
    recentlyRemovedMovies,
    
    // Pending actions
    pendingActions,
    getPendingAction,
    undoPendingAction,
    addPendingAction,
    removePendingAction,
    
    // State updaters - optimized for immediate updates
    setAllMovies,
    addMovie,
    updateMovie: addMovie,
    updateMovieState,
    removeMovieFromState,
    refetchMovies,
    addOrUpdateMovie: addMovie,
    
    // Visual state functions
    getVisualState,
    isPendingAction,
    
    // Legacy compatibility
    undoRemoval
  };
};
